import {
  agreementFromPartnerId,
  partnerFromAgreementCode,
} from "Store/partners/partnerUtils";
import {
  getCampaignAndLeadQueryParams,
  getQueryString,
} from "Utils/navigation/queryParameterUtils";
import {
  CustomerStatus,
  DiscountResponse,
  NewCartResponse,
  UserIdentificationPayload,
} from "./userIdentificationInterfaces";
import {
  GetDiscountRequest,
  NewCartRequest,
  NewCustomerRequest,
  UpdateCustomerInfoRequest,
} from "./userIdentificationTypes";

import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { productBackendNameToInsuranceName } from "Features/CheckPrice/Products/utils";
import { PartnerState } from "Store/partners/partnerInterfaces";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { AgreementCode } from "./agreementCodes";

export const createGetDiscountRequest = (
  payload: Required<Omit<UserIdentificationPayload, "onSuccess">>,
  partner: PartnerState,
): GetDiscountRequest => {
  const products =
    payload.products === ProductBackendName.ADVICE
      ? [
          ProductBackendName.DISABILITY,
          ProductBackendName.DEATH,
          ProductBackendName.CRITICAL_ILLNESS,
        ]
      : payload.products;
  const agreementProduct =
    payload.products === "pregnantandnewborn"
      ? AgreementCode.NORMAL_TARIFF
      : undefined;
  const agreementProductFromURL = agreementFromPartnerId(partner.partnerId);
  const discountUrlWithParams = `/open/insurance/discount${getQueryString(
    getCampaignAndLeadQueryParams(),
  )}`;
  return {
    data: {
      ssn: payload.ssn,
      products,
      agreementProduct,
      agreementProductFromURL,
    },
    url: discountUrlWithParams,
  };
};

export const createNewCartRequest = (
  payload: UserIdentificationPayload,
): NewCartRequest => {
  const userIsAuthenticated = isAuthenticated();
  const product = productBackendNameToInsuranceName(payload.products);
  return {
    data: userIsAuthenticated
      ? {
          initialProductVariant: `INSURANCE_${product}:STB-1`,
        }
      : undefined,
    url: userIsAuthenticated ? "/insurance/carts" : "/open/insurance/carts",
    open: !userIsAuthenticated,
  };
};

export const createUpdateCustomerInfoRequest = (
  discountResponse: DiscountResponse,
  newCartResponse: NewCartResponse,
): UpdateCustomerInfoRequest => ({
  data: {
    agreementProduct: discountResponse?.f2100?.agreementProduct,
    customerStatus:
      discountResponse?.customerStatus ?? CustomerStatus.CUSTOMER_EXISTING,
  },
  url: `${newCartResponse?._links.customer[0].href}/properties/customerInfo`,
});

export const createNewCustomerRequest = (
  payload: Required<Omit<UserIdentificationPayload, "onSuccess">>,
  newCartResponse: NewCartResponse,
): NewCustomerRequest => {
  const customerUrlWithParams = `${
    newCartResponse?._links.customer[0].href
  }${getQueryString(getCampaignAndLeadQueryParams())}`;
  const product = productBackendNameToInsuranceName(payload.products);
  return {
    data: {
      cartId: newCartResponse?.cartId,
      initialProductVariant: `INSURANCE_${product}:STB-1`,
      personInfo: {
        name: {
          firstName: payload.firstName,
          lastName: payload.lastName,
        },
        nin: payload.ssn,
      },
    },
    url: customerUrlWithParams,
  };
};

export const isPartnerMismatch = (
  cartAgreementProduct: number | undefined,
  partnerId: string,
): boolean => {
  return partnerFromAgreementCode(cartAgreementProduct) !== partnerId;
};
